import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseFullscreenOutlinedIcon from '@mui/icons-material/CloseFullscreenOutlined';
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { QuestionType, recommendationItem } from '../../NewIdeation/types';
import AccordionWrapper from '../../../../../common/components/Accordion/AccordionWrapper';
import Questions from './questions';
import EvaluationSkeleton from './EvaluationLoader';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface EvaluationReportV2Props {
  isOpen: boolean;
  onClose: () => void;
  loading: boolean;

  ReportObject?: {
    recommendations: recommendationItem[];
    questions: QuestionType[];
  };

  setReportObject: React.Dispatch<
    React.SetStateAction<{
      recommendations: recommendationItem[];
      questions: QuestionType[];
    }>
  >;

  isMinimized?: boolean;
  isFitToContent?: boolean;
  maxWidth?: string;
  module?: string;
  subtitle?: string;
  showQuestions?: boolean;
  requiredChipFields?: string[];
  fromSolutionXPLR?: boolean;
  evaluateSuccess?: boolean;
}

// ─── Internal normalised types ────────────────────────────────────────────────

type NormPoint = { text: string; isCritical: boolean };
type NormRec = { field_name: string; points: NormPoint[] };

// ─── Style constants (matching original EvaluationReport) ─────────────────────

const COLORS = {
  background: 'white',
  border: 'white',
  icon: '#000000',
  critical: '#FF8A05',
  optional: '#11953E',
  criticalBg: '#FEF2F2',
  optionalBg: '#F0FDF4',
  scrollbarTrack: '#f1f1f1',
  scrollbarThumb: '#c1c1c1',
  scrollbarThumbHover: '#a1a1a1',
  backdrop: 'rgba(0,0,0,0.4)',
} as const;

const SCROLLBAR_STYLES = {
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { backgroundColor: COLORS.scrollbarTrack, borderRadius: '4px' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: COLORS.scrollbarThumb, borderRadius: '4px' },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: COLORS.scrollbarThumbHover },
} as const;

const SCROLLBAR_STYLES_SMALL = {
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { backgroundColor: COLORS.scrollbarTrack, borderRadius: '3px' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: COLORS.scrollbarThumb, borderRadius: '3px' },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: COLORS.scrollbarThumbHover },
} as const;

const REQUIRED_CHIP_FIELDS = ['Current Process', 'Expected Impact'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** CRITICAL severity → required (red dot). Everything else → optional (green dot). */
function isCriticalSeverity(severity?: string): boolean {
  return (severity ?? '').toUpperCase() === 'CRITICAL';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalisePoint(raw: any): NormPoint {
  if (typeof raw === 'string') {
    return { text: raw, isCritical: false };
  }
  const severity: string = raw?.severity ?? '';
  const text: string =
    raw?.text ??
    raw?.description ??
    (() => {
      for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
        if (k === 'severity') continue;
        if (typeof v === 'string' && v) return v;
        if (typeof k === 'string' && k.length > 10) return k;
      }
      return '';
    })();
  return { text, isCritical: isCriticalSeverity(severity) };
}

function normaliseRec(item: recommendationItem): NormRec {
  const rawPoints: unknown[] = (item.points as unknown as unknown[]) ?? [];
  return {
    field_name: item.field_name,
    points: rawPoints.map(normalisePoint),
  };
}

// ─── Atoms ────────────────────────────────────────────────────────────────────

const Dot: React.FC<{
  isCritical: boolean;
  alignToTextTop?: boolean;
}> = ({ isCritical, alignToTextTop = false }) => (
  <Box
    component="span"
    sx={{
      width: 6,
      height: 6,
      minWidth: 6,
      minHeight: 6,
      borderRadius: '50%',
      backgroundColor: isCritical ? COLORS.critical : COLORS.optional,
      flexShrink: 0,
      ...(alignToTextTop && {
        position: 'relative',
        top: '0.45em',
      }),
    }}
  />
);

const Legend: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
    {(
      [
        { label: 'Critical', critical: true },
        { label: 'Optional', critical: false },
      ] as const
    ).map(({ label, critical }) => (
      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Dot isCritical={critical} />
        <Typography
          sx={{ fontSize: '16px', fontWeight: 400, color: '#000000', whiteSpace: 'nowrap' }}
        >
          {label}
        </Typography>
      </Box>
    ))}
  </Box>
);

/** Renders a recommendation card matching OpportunityCard's visual style but with severity dots */
const SeverityRecCard: React.FC<{ rec: NormRec; requiredFields: string[] }> = ({
  rec,
  requiredFields,
}) => {
  if (!rec.points.length) return null;
  const showChip = requiredFields.includes(rec.field_name);
  return (
    <Box
      sx={{
        border: '1px solid #D4DADF',
        borderRadius: '8px',
        p: 2,
        backgroundColor: '#fff',
        mx: 1,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '16px', mb: 1.5, color: '#000000' }}>
        {rec.field_name}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {rec.points.map((pt, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Dot isCritical={pt.isCritical} alignToTextTop />
            <Typography
              sx={{ fontSize: '16px', fontWeight: 400, color: '#1C1C1C', lineHeight: 1.4 }}
            >
              {pt.text}
              {showChip && pt.isCritical && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    ml: '2px',
                    px: '4px',
                    py: '1px',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#FF6E49',
                    border: '1px solid #FF6E49',
                    backgroundColor: '#FFE6E6',
                    borderRadius: '4px',
                    lineHeight: '18px',
                    verticalAlign: 'middle',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Required
                </Box>
              )}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const EvaluationReportV2: React.FC<EvaluationReportV2Props> = ({
  isOpen,
  onClose,
  loading,
  ReportObject,
  setReportObject,
  isMinimized = true,
  isFitToContent = false,
  maxWidth,
  subtitle = 'Recommendations and questions to help you improve your opportunity details form',
  showQuestions = true,
  requiredChipFields = REQUIRED_CHIP_FIELDS,
  fromSolutionXPLR = false,
  evaluateSuccess = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobileQuery = useMediaQuery('(max-width:600px)');
  const [isMobileLocked] = useState(isMobileQuery);
  const boxRef = useRef<HTMLDivElement>(null);

  const [reportData, setReportData] = useState(
    ReportObject ?? { recommendations: [] as recommendationItem[], questions: [] as QuestionType[] }
  );
  useEffect(() => {
    if (ReportObject) setReportData(ReportObject);
  }, [ReportObject, setReportData]);

  const normRecs = useMemo<NormRec[]>(
    () => (reportData.recommendations ?? []).map(normaliseRec),
    [reportData.recommendations]
  );

  const handleAnswerChange = useCallback(
    (questionId: string, value: string | string[]) => {
      setReportObject((prev) => ({
        ...prev,
        questions: prev.questions.map((q) => (q.id === questionId ? { ...q, answer: value } : q)),
      }));
    },
    [setReportObject]
  );

  const handleExpandToggle = useCallback(() => setIsExpanded((p) => !p), []);

  if (!isOpen) return null;

  // ── Sidebar content (mirrors original EvaluationReport structure) ──────────
  const SidebarContent = (
    <>
      {/* Recommendations accordion */}
      <Box sx={{ py: 1, pr: 2 }}>
        <AccordionWrapper
          title="Recommendations"
          headerColor="#EFF6FF"
          detailsPadding="8px 0px"
          headerPadding="0 16px"
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0, overflow: 'auto' }}>
            {loading ? (
              <EvaluationSkeleton />
            ) : normRecs.length > 0 ? (
              <>
                {/* Hint + legend row */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: evaluateSuccess ? 'flex-end' : 'flex-start',
                    gap: 1,
                    flexWrap: 'wrap',
                    px: 1,
                    pt: 0.5,
                  }}
                >
                  {!evaluateSuccess && (
                    <Typography sx={{ fontSize: '16px', color: '#000000', flex: 1 }}>
                      {'Implement required recommendations and revaluate to continue.'}
                    </Typography>
                  )}
                  <Legend />
                </Box>

                {normRecs.map((rec, i) => (
                  <SeverityRecCard key={i} rec={rec} requiredFields={requiredChipFields} />
                ))}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                No recommendations available.
              </Typography>
            )}
          </Box>
        </AccordionWrapper>
      </Box>

      {/* Questions accordion */}
      {showQuestions && (
        <Box sx={{ pt: 1, pr: 2 }}>
          <AccordionWrapper
            title="Questions"
            headerColor="#E8FFF6"
            detailsPadding="8px 0px"
            headerPadding="0 16px"
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, overflow: 'auto' }}>
              {loading ? (
                <EvaluationSkeleton />
              ) : reportData.questions?.length > 0 ? (
                reportData.questions.map((item, index) => (
                  <Questions
                    key={item.id}
                    index={index}
                    item={item}
                    value={item.answer ?? (item.type === 'MULTI_SELECT' ? [] : '')}
                    onChange={handleAnswerChange}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  No questions available.
                </Typography>
              )}
            </Box>
          </AccordionWrapper>
        </Box>
      )}
    </>
  );

  // ── Shared header (same structure as original) ─────────────────────────────
  const PanelHeader = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '16px', lineHeight: '100%' }}>
        Evaluation Report
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* <IconButton
          onClick={handleExpandToggle}
          sx={{ color: COLORS.icon, p: 1 }}
          aria-label={isExpanded ? 'Exit Fullscreen' : 'Expand'}
        >
          {isExpanded ? <CloseFullscreenOutlinedIcon /> : <FullscreenIcon />}
        </IconButton> */}
        <IconButton
          onClick={onClose}
          sx={{ color: COLORS.icon, px: 0.5, py: 0.5 }}
          aria-label="Close"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );

  const PanelSubtitle = (
    <Typography
      ref={boxRef}
      sx={{
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '140%',
        color: '#1C1C1C',
      }}
    >
      {subtitle}
    </Typography>
  );

  // ── Fullscreen ─────────────────────────────────────────────────────────────
  if (isExpanded) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'white',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            pt: 2,
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '16px', lineHeight: '100%' }}>
            Evaluation Report
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={handleExpandToggle} sx={{ color: COLORS.icon, p: 1 }}>
              <CloseFullscreenOutlinedIcon />
            </IconButton>
            <IconButton onClick={onClose} sx={{ color: COLORS.icon, p: 1 }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '140%',
            color: '#6B7280',
            px: 3,
            py: 1,
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          {subtitle}
        </Typography>
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              backgroundColor: 'white',
              p: 3,
              ...SCROLLBAR_STYLES,
            }}
          >
            {SidebarContent}
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Mobile bottom sheet ────────────────────────────────────────────────────
  if (isMobileLocked) {
    return (
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300 }}>
        <Box
          onClick={onClose}
          sx={{ position: 'absolute', inset: 0, backgroundColor: COLORS.backdrop }}
        />
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '72vh',
            backgroundColor: 'white',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 -4px 16px rgba(0,0,0,0.2)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '16px', lineHeight: '100%' }}>
              Evaluation Report
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={handleExpandToggle} sx={{ color: COLORS.icon, p: 1 }}>
                <FullscreenIcon />
              </IconButton>
              <IconButton onClick={onClose} sx={{ color: COLORS.icon, p: 1 }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '140%',
              color: '#6B7280',
              px: 2,
              pb: 1,
            }}
          >
            {subtitle}
          </Typography>
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 1 }}>
              {SidebarContent}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Sidebar default (mirrors original EvaluationReport exactly) ───────────
  return (
    <Box
      sx={{
        backgroundColor: COLORS.background,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 2,
        px: fromSolutionXPLR ? 0 : 1,
        pt: fromSolutionXPLR ? 0 : 2,
        pb: 2,
        pr: fromSolutionXPLR ? 0 : 1,
        width: '100%',
        maxWidth: maxWidth,
        position: 'sticky',
        top: isMinimized ? 0 : 68,
        alignSelf: 'flex-start',
        overflow: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        opacity: 1,
        flexShrink: 0,
        maxHeight: fromSolutionXPLR ? '52vh' : '1000px',
        ...((isMinimized || isFitToContent) && { height: '100%' }),
      }}
    >
      <Box sx={{ px: fromSolutionXPLR ? 1 : 0 }}>
        {PanelHeader}
        {PanelSubtitle}
      </Box>
      <Box
        sx={{
          backgroundColor: 'white',
          border: `1px solid ${COLORS.border}`,
          borderRadius: 2,
          maxHeight: 'calc(100vh - 250px)',
          overflowY: 'auto',
          boxSizing: 'border-box',
          width: '100%',
          ...SCROLLBAR_STYLES_SMALL,
        }}
      >
        {SidebarContent}
      </Box>
    </Box>
  );
};

export default EvaluationReportV2;
